import React, { useState } from 'react'
import DOMPurify from 'dompurify'
import { ChatMessage as Message, ChatAttachment } from '../../hooks/useChatThread'
import { safeInitial } from '../../utils/safeString'
import { toPlainText } from '../../utils/messageText'
import { MessageForwardActions, type ForwardSource } from './MessageForwardActions'

/* ─── Rich (HTML) message body ───────────────────────────────────────────────
 * Agent rich replies arrive with message_type === 'html' and a `content` that
 * is already server-sanitized HTML. We sanitize again here with DOMPurify
 * (defense in depth) and render inside a `.prose` container so lists, links,
 * blockquotes, and emphasis display properly. Links open in a new tab.
 */
const HtmlMessageBody: React.FC<{ html: string; outgoing: boolean }> = ({ html, outgoing }) => {
  const clean = React.useMemo(
    () =>
      DOMPurify.sanitize(html, {
        ADD_ATTR: ['target', 'rel'],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
      }),
    [html]
  )
  return (
    <div
      className={`prose prose-sm max-w-none break-words ${
        outgoing
          ? 'prose-invert prose-a:text-white prose-a:underline'
          : 'dark:prose-invert prose-a:text-blue-500'
      }`}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  )
}

const isHtmlMessage = (m: Message): boolean => m.messageType === 'html' && !!m.content

/* ─── URL linkification ──────────────────────────────────────────────── */
/**
 * `outgoing` = the bubble has a coloured (brand-blue) background with white text.
 * On that background a blue link is unreadable, so links render white+underline
 * (matching the surrounding text). On light bubbles (incoming, whisper, WhatsApp)
 * links stay blue for the usual affordance.
 */
const linkifyText = (text: string, outgoing = false): React.ReactNode => {
  const urlRegex = /(https?:\/\/[^\s<>]+|www\.[^\s<>]+)/gi
  const parts = text.split(urlRegex)
  if (parts.length === 1) return text

  const linkClass = outgoing
    ? 'text-white hover:text-white/80 underline break-all'
    : 'text-blue-500 hover:text-blue-600 underline break-all'

  return parts.map((part, i) => {
    if (/^https?:\/\//i.test(part) || /^www\./i.test(part)) {
      const href = part.startsWith('www.') ? `https://${part}` : part
      const cleanHref = href.replace(/[)}\].,;:!?]+$/, '')
      const cleanText = part.replace(/[)}\].,;:!?]+$/, '')
      const trailing = part.slice(cleanText.length)
      return (
        <React.Fragment key={i}>
          <a
            href={cleanHref}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            onClick={(e) => e.stopPropagation()}
          >
            {cleanText}
          </a>
          {trailing}
        </React.Fragment>
      )
    }
    return <React.Fragment key={i}>{part}</React.Fragment>
  })
}

/* ─── Time formatter ─────────────────────────────────────────────────── */
const formatTime = (dateStr: string) => {
  const d = new Date(dateStr)
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

/* ─── Sender colors (WhatsApp-style) ─────────────────────────────────── */
const senderColors = [
  '#e06c75', '#06cf9c', '#53bdeb', '#e9a032',
  '#7c6bff', '#f06292', '#26c6da', '#ff7043',
]
const getSenderColor = (name: string) => {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return senderColors[Math.abs(hash) % senderColors.length]
}

interface ChatMessageProps {
  message: Message
  prevMessage?: Message
  isWhatsApp?: boolean
  onExpandImage?: (url: string) => void
  /**
   * Source chat/ticket this bubble belongs to. When present, non-system text
   * bubbles get a Copy + "Forward to Copilot" hover row. Absent (e.g. WhatsApp
   * views) → only Copy is offered (no forward), via `text`-only actions.
   */
  forwardSource?: ForwardSource
  /** Save an edit to one of YOUR OWN sent messages. */
  onEditMessage?: (messageId: string, body: string) => Promise<void>
  /** Open the version timeline for a message that has been edited. */
  onViewVersions?: (messageId: string) => void
  /** Your agent id — editing is gated to your own messages. */
  currentAgentId?: string
  /** The visitor's real name — shown on their messages instead of "Customer". */
  visitorName?: string
  /** The visitor's company logo — used as the avatar on their messages. */
  companyLogoUrl?: string
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  prevMessage,
  isWhatsApp = false,
  onExpandImage,
  forwardSource,
  onEditMessage,
  onViewVersions,
  currentAgentId,
  visitorName,
  companyLogoUrl,
}) => {
  const [expandedImage, setExpandedImage] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  const isOutgoing = message.sender === 'agent' || message.sender === 'bot'
  const isSystem = message.sender === 'system'
  const isWhisper = message.sender === 'whisper'

  // The visitor's real name on their own messages (falls back to whatever the
  // message carried, then the generic label) — so it never just says "Customer".
  const displaySenderName =
    message.sender === 'customer' && visitorName && visitorName.trim()
      ? visitorName.trim()
      : message.senderName

  // Plain-text version of the body for Copy/Forward (strips HTML for rich
  // agent replies). Empty for attachment-only bubbles → actions self-hide.
  const plainText = toPlainText(message.content || '', message.messageType)

  // You may only edit messages YOU sent.
  const isMine =
    message.sender === 'agent' &&
    (!currentAgentId || !message.senderId || String(message.senderId) === String(currentAgentId))
  const canEdit = !!onEditMessage && isMine && !!plainText && !isSystem && !isWhisper

  const beginEdit = () => {
    setDraft(toPlainText(message.content || '', message.messageType))
    setEditing(true)
  }

  const saveEdit = async () => {
    if (!onEditMessage || !draft.trim() || savingEdit) return
    setSavingEdit(true)
    try {
      await onEditMessage(String(message.id), draft.trim())
      setEditing(false)
    } catch {
      /* the hook surfaces the error */
    } finally {
      setSavingEdit(false)
    }
  }

  const handleImageClick = (url: string) => {
    if (onExpandImage) onExpandImage(url)
    else setExpandedImage(url)
  }

  /* ── System messages ────────────────────────────────────────────── */
  if (isSystem) {
    return isWhatsApp ? (
      <div className="flex items-center justify-center my-3 px-4">
        <span
          className="px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm"
          style={{ backgroundColor: '#d1f4cc', color: '#1b4332' }}
        >
          {message.content}
          <span className="ml-2 opacity-60">{formatTime(message.createdAt)}</span>
        </span>
      </div>
    ) : (
      <div className="flex items-center justify-center my-2 px-4">
        <span className={`px-3 py-1 text-[11px] font-medium text-txt-tertiary bg-surface-secondary border border-border ${message.content?.includes('\n') ? 'rounded-lg text-left whitespace-pre-line max-w-xs' : 'rounded-full'}`}>
          {message.content}
          <span className="ml-1.5 opacity-60">{formatTime(message.createdAt)}</span>
        </span>
      </div>
    )
  }

  /* ── Whisper messages: internal note styling (same for both) ─────── */
  if (isWhisper) {
    return (
      <>
        <div className="flex items-start gap-2 mb-2 px-4" style={{ paddingLeft: '40px' }}>
          <div className="relative max-w-[75%]">
            <div
              className="relative rounded-lg px-2.5 py-1.5 shadow-sm border-l-4"
              style={{
                backgroundColor: '#fef9c3',
                borderLeftColor: '#f59e0b',
              }}
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <svg className="w-3 h-3" style={{ color: '#b45309' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-[11px] font-bold uppercase" style={{ color: '#b45309' }}>Whisper</span>
                <span className="text-[11px] font-semibold" style={{ color: '#92400e' }}>{message.senderName}</span>
              </div>
              <p className="text-[14.2px] whitespace-pre-wrap [overflow-wrap:break-word] [word-break:normal]" style={{ color: '#78350f', lineHeight: '1.35' }}>
                {linkifyText(message.content)}
              </p>
              <p className="text-[11px] mt-0.5 text-right" style={{ color: '#b45309' }}>
                {formatTime(message.createdAt)}
              </p>
            </div>
          </div>
        </div>
        <ExpandedImageModal url={expandedImage} onClose={() => setExpandedImage(null)} />
      </>
    )
  }

  /* ── WhatsApp-style messages ────────────────────────────────────── */
  if (isWhatsApp) {
    const showSender = !isOutgoing && (!prevMessage || prevMessage.sender !== message.sender || prevMessage.sender === 'system')
    const showOutgoingTail = isOutgoing && (!prevMessage || prevMessage.sender !== message.sender || prevMessage.sender === 'system')

    return (
      <>
        <div
          className="group flex items-start gap-2 mb-0.5"
          style={{
            paddingLeft: isOutgoing ? '40px' : '12px',
            paddingRight: isOutgoing ? '12px' : '40px',
            justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
          }}
        >
          <div className="relative max-w-[75%] flex flex-col items-stretch">
            <div className="relative">
              {/* WhatsApp tail — incoming (left) */}
              {!isOutgoing && showSender && (
                <div
                  className="absolute top-0 w-3 h-3"
                  style={{ left: '-6px', background: '#ffffff', clipPath: 'polygon(0 0, 100% 0, 100% 100%)' }}
                />
              )}
              {/* WhatsApp tail — outgoing (right) */}
              {isOutgoing && showOutgoingTail && (
                <div
                  className="absolute top-0 w-3 h-3"
                  style={{ right: '-6px', background: '#d9fdd3', clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                />
              )}

              {/* Bubble */}
              <div
                className="relative rounded-lg px-2.5 py-1.5 shadow-sm"
                style={{
                  backgroundColor: isOutgoing ? '#d9fdd3' : '#ffffff',
                  borderTopLeftRadius: !isOutgoing && showSender ? '0' : undefined,
                  borderTopRightRadius: isOutgoing && showOutgoingTail ? '0' : undefined,
                }}
              >
                {showSender && (
                  <p className="text-[13px] font-semibold mb-0.5" style={{ color: getSenderColor(displaySenderName || ''), lineHeight: '1.3' }}>
                    {displaySenderName}
                  </p>
                )}

                {message.isInternal && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase mb-1" style={{ backgroundColor: '#fef3c7', color: '#b45309' }}>
                    Internal
                  </span>
                )}

                {message.attachments.length > 0 && (
                  <div className="mb-1">
                    {message.attachments.map((att) => (
                      <AttachmentPreview key={att.id} attachment={att} onImageClick={handleImageClick} isOutgoing={isOutgoing} variant="whatsapp" />
                    ))}
                  </div>
                )}

                {message.content &&
                  (isHtmlMessage(message) ? (
                    <div className="prose prose-sm max-w-none break-words" style={{ color: '#111b21' }}>
                      <HtmlMessageBody html={message.content} outgoing={false} />
                    </div>
                  ) : (
                    <p className="text-[14.2px] whitespace-pre-wrap [overflow-wrap:break-word] [word-break:normal]" style={{ color: '#111b21', lineHeight: '1.35' }}>
                      {linkifyText(message.content)}
                    </p>
                  ))}

                {/* Timestamp + read receipt */}
                <p className="text-[11px] mt-0.5 text-right flex items-center justify-end gap-1" style={{ color: isOutgoing ? '#1b7e4e' : '#667781' }}>
                  {formatTime(message.createdAt)}
                  {isOutgoing && (
                    <span className="inline-block" style={{ color: message.readAt ? '#53bdeb' : '#b0b7bc' }}>
                      &#10003;&#10003;
                    </span>
                  )}
                </p>
              </div>
            </div>
            {plainText && (
              <MessageForwardActions
                text={plainText}
                source={forwardSource}
                align={isOutgoing ? 'end' : 'start'}
                messageId={message.id}
              />
            )}
          </div>
        </div>
        <ExpandedImageModal url={expandedImage} onClose={() => setExpandedImage(null)} />
      </>
    )
  }

  /* ══════════════════════════════════════════════════════════════════ */
  /*  DEFAULT / LIVE CHAT STYLE                                        */
  /* ══════════════════════════════════════════════════════════════════ */

  // Determine sender style for live chat.
  // NOTE: previously both styles used `text-txt-primary`, which is near-white
  // in dark mode AND the outgoing bubble background `bg-brand-50` is also
  // near-white — net effect: invisible text. The fix uses an explicit brand
  // background with white text for outgoing (readable in both themes), and
  // a darker neutral surface for incoming so primary text contrasts.
  const style = isOutgoing
    ? { align: 'justify-end' as const, bubble: 'bg-brand-500 border border-brand-600', text: 'text-white', nameColor: 'text-brand-600' }
    : { align: 'justify-start' as const, bubble: 'bg-surface-secondary border border-border', text: 'text-txt-primary', nameColor: 'text-txt-secondary' }

  const showSenderLiveChat = !isOutgoing && (!prevMessage || prevMessage.sender !== message.sender || prevMessage.sender === 'system')

  // AGENT-side attribution (Lee, 14 Aug 2026): every agent message shows WHO
  // sent it — profile icon + name on the right — and the group breaks whenever
  // a DIFFERENT agent continues (senderId change), so a colleague taking over
  // mid-chat is visible per message, not just in the header.
  const showAgentSender =
    isOutgoing &&
    (!prevMessage ||
      prevMessage.sender !== message.sender ||
      prevMessage.sender === 'system' ||
      String(prevMessage.senderId ?? prevMessage.senderName ?? '') !== String(message.senderId ?? message.senderName ?? ''))

  return (
    <>
      <div className={`group flex ${style.align} mb-2 px-4`}>
        {/* Avatar for incoming — the company logo if we have it, else a neutral
            initial (no loud colour). */}
        {!isOutgoing && showSenderLiveChat && (
          companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt=""
              className="w-8 h-8 rounded-full object-cover bg-surface-secondary border border-border mr-2 shrink-0 mt-0.5"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-surface-secondary border border-border flex items-center justify-center mr-2 shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-txt-secondary">
                {safeInitial(displaySenderName)}
              </span>
            </div>
          )
        )}
        {!isOutgoing && !showSenderLiveChat && (
          <div className="w-8 mr-2 shrink-0" /> /* spacer for alignment */
        )}

        <div className="max-w-[75%] min-w-0">
          {/* Sender name */}
          {showSenderLiveChat && (
            <p className="text-[13px] font-semibold mb-1 text-txt-primary">
              {displaySenderName}
              {message.sender === 'bot' && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">Bot</span>
              )}
            </p>
          )}
          {showAgentSender && message.senderName && (
            <p className="text-[13px] font-semibold mb-1 text-right text-txt-primary">
              {message.senderName}
            </p>
          )}

          {/* Bubble */}
          <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap [overflow-wrap:break-word] [word-break:normal] ${style.bubble} ${style.text}`}>
            {message.isInternal && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-yellow-50 text-yellow-700 mb-1">
                Internal
              </span>
            )}

            {/* Attachments */}
            {message.attachments.length > 0 && (
              <div className="mb-2">
                {message.attachments.map((att) => (
                  <AttachmentPreview key={att.id} attachment={att} onImageClick={handleImageClick} isOutgoing={isOutgoing} variant="default" />
                ))}
              </div>
            )}

            {editing ? (
              <div className="flex flex-col gap-1.5">
                <textarea
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void saveEdit() }
                    if (e.key === 'Escape') { e.preventDefault(); setEditing(false) }
                  }}
                  rows={Math.min(6, Math.max(2, draft.split('\n').length))}
                  className="w-full rounded-md px-2 py-1.5 text-sm text-gray-900 bg-white outline-none resize-y"
                />
                <div className="flex items-center gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="text-[11px] font-semibold opacity-80 hover:opacity-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={!draft.trim() || savingEdit}
                    className="text-[11px] font-bold px-2 py-0.5 rounded bg-white/95 text-brand-700 disabled:opacity-50"
                  >
                    {savingEdit ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              message.content &&
              (isHtmlMessage(message) ? (
                <HtmlMessageBody html={message.content} outgoing={isOutgoing} />
              ) : (
                linkifyText(message.content, isOutgoing)
              ))
            )}
          </div>

          {/* Timestamp */}
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-[11px] text-txt-secondary">{formatTime(message.createdAt)}</span>
            {message.readAt && isOutgoing && (
              <span className="text-[11px] text-green-500 flex items-center gap-0.5">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                Read
              </span>
            )}
          </div>

          {plainText && !editing && (
            <MessageForwardActions
              text={plainText}
              source={forwardSource}
              align={isOutgoing ? 'end' : 'start'}
              messageId={message.id}
              onEdit={canEdit ? beginEdit : undefined}
              onViewVersions={
                message.edited && onViewVersions ? () => onViewVersions(String(message.id)) : undefined
              }
            />
          )}
        </div>

        {/* Avatar for outgoing (agent) — WHO sent this message */}
        {isOutgoing && showAgentSender && (
          <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center ml-2 shrink-0 mt-0.5 overflow-hidden" title={message.senderName}>
            {message.senderAvatar ? (
              <img src={message.senderAvatar} alt={message.senderName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs font-semibold text-brand-700 dark:text-brand-300">
                {safeInitial(message.senderName)}
              </span>
            )}
          </div>
        )}
        {isOutgoing && !showAgentSender && (
          <div className="w-8 ml-2 shrink-0" /> /* spacer keeps bubbles aligned */
        )}
      </div>
      <ExpandedImageModal url={expandedImage} onClose={() => setExpandedImage(null)} />
    </>
  )
}

/* ─── Expanded image modal ────────────────────────────────────────────── */
const ExpandedImageModal: React.FC<{ url: string | null; onClose: () => void }> = ({ url, onClose }) => {
  if (!url) return null
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm cursor-pointer"
      onClick={onClose}
    >
      <img src={url} alt="Expanded" className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-float" />
    </div>
  )
}

/* ─── Attachment preview ──────────────────────────────────────────────── */

interface AttachmentPreviewProps {
  attachment: ChatAttachment
  onImageClick: (url: string) => void
  isOutgoing: boolean
  variant: 'whatsapp' | 'default'
}

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({ attachment, onImageClick, isOutgoing, variant }) => {
  if (attachment.type === 'image') {
    return (
      <div
        className="cursor-pointer rounded-md overflow-hidden hover:opacity-90 transition-opacity mb-1"
        onClick={() => onImageClick(attachment.url)}
      >
        <img src={attachment.url} alt={attachment.filename} className="max-w-full" style={{ maxHeight: '300px' }} />
      </div>
    )
  }

  if (attachment.type === 'video') {
    return (
      <div className="rounded-md overflow-hidden mb-1">
        <video controls className="max-w-full bg-black" style={{ maxHeight: '300px' }}>
          <source src={attachment.url} type="video/mp4" />
        </video>
      </div>
    )
  }

  // File attachment — WhatsApp-style document card
  const docName = attachment.filename || 'Document'
  const ext = docName.split('.').pop()?.toLowerCase() || ''
  const sizeStr = attachment.size > 1024 * 1024 ? `${(attachment.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(attachment.size / 1024)} KB`
  const isPdf = ext === 'pdf'
  const isDoc = ['doc', 'docx'].includes(ext)
  const isXls = ['xls', 'xlsx', 'csv'].includes(ext)
  const typeLabel = isPdf ? 'PDF' : isDoc ? 'DOCX' : isXls ? 'XLS' : ext.toUpperCase() || 'FILE'
  const iconColor = isPdf ? '#E53935' : isDoc ? '#1565C0' : isXls ? '#2E7D32' : '#546E7A'

  if (variant === 'whatsapp') {
    const bgColor = isOutgoing ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)'
    return (
      <a href={attachment.url} download
        className="flex items-center gap-3 rounded-lg mb-1 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
        style={{ backgroundColor: bgColor, minWidth: '240px', maxWidth: '320px', textDecoration: 'none' }}
      >
        <div className="flex items-center justify-center shrink-0" style={{ width: '56px', height: '56px', backgroundColor: iconColor }}>
          {isPdf ? (
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/></svg>
          ) : (
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
          )}
        </div>
        <div className="flex-1 min-w-0 py-2 pr-3">
          <p className="text-[13px] font-medium truncate" style={{ color: '#111b21' }}>{docName}</p>
          <p className="text-[11px] mt-0.5" style={{ color: '#667781' }}>{sizeStr} &middot; {typeLabel}</p>
        </div>
      </a>
    )
  }

  // Default live chat — document card style
  return (
    <a href={attachment.url} download
      className="flex items-center gap-3 rounded-lg mb-1 overflow-hidden border border-border hover:bg-gray-50 transition-colors"
      style={{ minWidth: '220px', maxWidth: '300px', textDecoration: 'none' }}
    >
      <div className="flex items-center justify-center shrink-0 rounded-l-lg" style={{ width: '48px', height: '48px', backgroundColor: iconColor }}>
        {isPdf ? (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/></svg>
        ) : (
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
        )}
      </div>
      <div className="flex-1 min-w-0 py-2 pr-3">
        <p className="text-xs font-medium text-txt-primary truncate">{docName}</p>
        <p className="text-[10px] text-txt-tertiary">{sizeStr} &middot; {typeLabel}</p>
      </div>
    </a>
  )
}
